import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Plus, ChevronLeft, ChevronRight, X, Loader2, CheckCircle2, Pencil, ToggleLeft, ToggleRight, Gift, AlertTriangle, Search, ArrowRight, Coins, Sprout, Download } from 'lucide-react';
import { exportCsv } from '../lib/exportCsv';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth.store';

const inputCls = 'w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/40 transition-colors placeholder-slate-700';

interface Holder {
  id: string;
  name: string;
  email: string;
  worldIds: string[];
  isActive: boolean;
  createdAt: string;
  balances: { worldId: string; currency: string; balance: string; updatedAt: string }[];
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${active ? 'badge-active' : 'badge-inactive'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-400 pulse-dot' : 'bg-slate-500'}`} />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function EditHolderModal({ holder, onClose }: { holder: Holder; onClose: () => void }) {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.role);
  const authWorldIds = useAuthStore((s) => s.worldIds);
  const [name, setName] = useState(holder.name);
  const [email, setEmail] = useState(holder.email);
  const [selectedWorldIds, setSelectedWorldIds] = useState<string[]>(holder.worldIds);
  const [saved, setSaved] = useState(false);

  const { data: worlds = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  const worldOptions = role === 'WORLD_OWNER'
    ? worlds.filter((w) => authWorldIds.includes(w.id))
    : worlds;

  const toggleWorld = (worldId: string) => {
    setSelectedWorldIds((prev) =>
      prev.includes(worldId) ? prev.filter((id) => id !== worldId) : [...prev, worldId],
    );
  };

  const mutation = useMutation({
    mutationFn: (data: { name: string; email: string; worldIds: string[] }) =>
      api.patch(`/holders/${holder.id}`, data).then((r) => r.data),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['holders'] });
      qc.invalidateQueries({ queryKey: ['holder', holder.id] });
      setTimeout(onClose, 1200);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedWorldIds.length === 0) return;
    mutation.mutate({ name: name.trim(), email: email.trim().toLowerCase(), worldIds: selectedWorldIds });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        className="relative w-full max-w-lg glass-card p-6 z-10 overflow-y-auto max-h-[90vh]"
        style={{ borderColor: 'rgba(0,212,255,0.2)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Pencil className="w-4 h-4 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Edit Holder</h3>
              <p className="text-[10px] text-slate-600 font-mono">{holder.id.slice(0, 16)}…</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-300 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {saved ? (
          <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
            <CheckCircle2 className="w-4 h-4" />
            Changes saved
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Full Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
                maxLength={100}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Worlds <span className="text-slate-700 normal-case">(select all that apply)</span>
              </label>
              {worldOptions.length === 0 ? (
                <p className="text-xs text-slate-600">No worlds available</p>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {worldOptions.map((w) => (
                    <label
                      key={w.id}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-white/8 hover:border-cyan-500/20 hover:bg-cyan-500/5 cursor-pointer transition-all"
                    >
                      <input
                        type="checkbox"
                        checked={selectedWorldIds.includes(w.id)}
                        onChange={() => toggleWorld(w.id)}
                        className="accent-cyan-500 w-3.5 h-3.5"
                      />
                      <span className="text-xs text-slate-300">{w.name}</span>
                    </label>
                  ))}
                </div>
              )}
              {selectedWorldIds.length === 0 && (
                <p className="text-[10px] text-amber-500/80 mt-1">Select at least one world</p>
              )}
            </div>
            {mutation.isError && (
              <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Update failed.'}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-slate-400 border border-white/8 hover:border-white/15 hover:text-slate-300 transition-all">
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isPending || selectedWorldIds.length === 0}
                className="flex-1 btn-primary flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
              >
                {mutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : <><Pencil className="w-3.5 h-3.5" /> Save Changes</>}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

/* ─── Seed UKC Modal (CB Admin only) ─── */
function SeedUkcModal({ holder, onClose }: { holder: Holder; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: worlds = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });
  const available = worlds.filter((w) => holder.worldIds.includes(w.id));
  const [worldId, setWorldId] = useState(available[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: () => api.post('/central-bank/seed-holder', { holderId: holder.id, worldId, amount, note }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['holders'] });
      setDone(true);
    },
  });

  const valid = worldId && amount && Number(amount) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        className="w-full max-w-md bg-[#0d1829] border border-white/10 rounded-2xl p-6 shadow-2xl"
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-yellow-400" />
            <span className="font-bold text-white">Seed UKC Balance</span>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        </div>

        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="text-white font-semibold">Balance seeded!</p>
            <p className="text-slate-500 text-sm mt-1">{amount} UKC credited to {holder.name}</p>
            <button onClick={onClose} className="mt-5 btn-primary text-sm px-6 py-2 rounded-xl">Done</button>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
            <p className="text-xs text-slate-500">Debits CB reserve; directly credits <span className="text-cyan-400">{holder.name}</span>'s UKC balance in the selected world.</p>
            <div>
              <label className="block text-xs text-slate-500 mb-1">World</label>
              <select value={worldId} onChange={(e) => setWorldId(e.target.value)} className={inputCls}>
                {available.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Amount (UKC)</label>
              <input type="number" min="0.00000001" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Note (optional)</label>
              <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for seeding" className={inputCls} />
            </div>
            {mutation.isError && (
              <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Seeding failed.'}
              </p>
            )}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-slate-400 border border-white/8 hover:border-white/15 hover:text-slate-300 transition-all">Cancel</button>
              <button type="submit" disabled={!valid || mutation.isPending} className="flex-1 btn-primary flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">
                {mutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Seeding…</> : <><Coins className="w-3.5 h-3.5" /> Seed Balance</>}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

/* ─── Fund From World Modal (WORLD_OWNER only) ─── */
function FundFromWorldModal({ holder, onClose }: { holder: Holder; onClose: () => void }) {
  const qc = useQueryClient();
  const authWorldIds = useAuthStore((s) => s.worldIds);
  const { data: worlds = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });
  // Worlds the partner owns AND the holder is member of
  const available = worlds.filter((w) => authWorldIds.includes(w.id) && holder.worldIds.includes(w.id));
  const [worldId, setWorldId] = useState(available[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [done, setDone] = useState(false);

  const { data: accounts = [] } = useQuery<{ accountType: string; balance: string }[]>({
    queryKey: ['world-accounts', worldId],
    queryFn: () => api.get(`/worlds/${worldId}/accounts`).then((r) => r.data),
    enabled: !!worldId,
  });
  const ownerRevenue = accounts.find((a) => a.accountType === 'OWNER_REVENUE');
  const maxBal = Number(ownerRevenue?.balance ?? 0);

  const mutation = useMutation({
    mutationFn: () => api.post(`/holders/${holder.id}/fund`, { worldId, amount, sourceAccount: 'OWNER_REVENUE' }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['holders'] });
      qc.invalidateQueries({ queryKey: ['world-accounts', worldId] });
      setDone(true);
    },
  });

  const amtNum = Number(amount);
  const valid = worldId && amtNum > 0 && amtNum <= maxBal;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        className="w-full max-w-md bg-[#0d1829] border border-white/10 rounded-2xl p-6 shadow-2xl"
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Sprout className="w-5 h-5 text-emerald-400" />
            <span className="font-bold text-white">Fund Holder from World</span>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        </div>

        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="text-white font-semibold">Holder funded!</p>
            <p className="text-slate-500 text-sm mt-1">{amount} UKC sent to {holder.name}</p>
            <button onClick={onClose} className="mt-5 btn-primary text-sm px-6 py-2 rounded-xl">Done</button>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
            <p className="text-xs text-slate-500">Debits your world's <span className="text-emerald-400">Owner Revenue</span> account and credits <span className="text-cyan-400">{holder.name}</span>'s holder balance.</p>
            {available.length === 0 && (
              <p className="text-amber-400 text-xs bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2">This holder has no worlds in common with your account.</p>
            )}
            <div>
              <label className="block text-xs text-slate-500 mb-1">World</label>
              <select value={worldId} onChange={(e) => setWorldId(e.target.value)} className={inputCls}>
                {available.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            {ownerRevenue && (
              <p className="text-xs text-slate-500">
                Owner Revenue balance: <span className="text-emerald-400 font-semibold">{Number(ownerRevenue.balance).toFixed(8)} UKC</span>
              </p>
            )}
            <div>
              <label className="block text-xs text-slate-500 mb-1">Amount (UKC)</label>
              <input type="number" min="0.00000001" max={maxBal} step="any" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={inputCls} />
            </div>
            {mutation.isError && (
              <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Funding failed.'}
              </p>
            )}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-slate-400 border border-white/8 hover:border-white/15 hover:text-slate-300 transition-all">Cancel</button>
              <button type="submit" disabled={!valid || mutation.isPending || available.length === 0} className="flex-1 btn-primary flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">
                {mutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Funding…</> : <><Sprout className="w-3.5 h-3.5" /> Fund Holder</>}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {Array.from({ length: 6 }).map((_, i) => (
        <td key={i} className="px-5 py-4">
          <div className="shimmer h-4 rounded w-3/4" />
        </td>
      ))}
    </tr>
  );
}

/* ─── Gift / Tip Modal ─── */
function GiftTipModal({ fromHolder, onClose }: { fromHolder: Holder; onClose: () => void }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [toHolderId, setToHolderId] = useState('');
  const [worldId, setWorldId] = useState('');          // kept for fee-config query
  const [balanceKey, setBalanceKey] = useState('');   // composite "worldId:currency"
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [done, setDone] = useState(false);

  const { data: allHolders = [] } = useQuery<Holder[]>({
    queryKey: ['holders'],
    queryFn: () => api.get('/holders').then((r) => r.data),
  });

  // Central Bank tip rev-share config
  const { data: cbConfig } = useQuery<{ tipRevSharePct: string }>({
    queryKey: ['cb-config'],
    queryFn: () => api.get('/central-bank/config').then((r) => r.data).catch(() => ({ tipRevSharePct: '30' })),
  });

  // World financial config (revenueShare, taxShare, taxOwnerId) when a world is selected
  const { data: worldConfig } = useQuery<{
    revenueShare: string; taxShare: string; taxOwnerId: string | null;
  }>({
    queryKey: ['world-fee-config', worldId],
    queryFn: () => api.get(`/worlds/${worldId}`).then((r) => r.data),
    enabled: !!worldId,
    select: (d) => ({
      revenueShare: String(d.revenueShare ?? '0'),
      taxShare: String(d.taxShare ?? '0'),
      taxOwnerId: d.taxOwnerId ?? null,
    }),
  });

  const filteredRecipients = useMemo(() => {
    const q = search.toLowerCase();
    return allHolders.filter(
      (h) => h.id !== fromHolder.id && (
        h.name.toLowerCase().includes(q) ||
        h.email.toLowerCase().includes(q) ||
        h.id.toLowerCase().includes(q)
      ),
    );
  }, [allHolders, fromHolder.id, search]);

  // Balances available on the source holder
  const balances = fromHolder.balances ?? [];
  const [selectedBalWorldId, selectedBalCurrency] = balanceKey.includes(':')
    ? [balanceKey.split(':')[0], balanceKey.split(':').slice(1).join(':')]
    : ['', ''];
  const selectedBalance = balances.find((b) => b.worldId === selectedBalWorldId && b.currency === selectedBalCurrency);
  const available = selectedBalance ? parseFloat(selectedBalance.balance) : 0;
  const amountNum = parseFloat(amount) || 0;
  const overBalance = amountNum > available;

  // ── Live fee breakdown ──────────────────────────────────────────────────
  const fees = useMemo(() => {
    if (!amountNum || amountNum <= 0 || !worldConfig) return null;

    const revSharePct   = parseFloat(worldConfig.revenueShare ?? '0') / 100;
    const cbCutPct      = parseFloat(cbConfig?.tipRevSharePct ?? '30') / 100;
    const taxPct        = parseFloat(worldConfig.taxShare ?? '0') / 100;

    const worldRevChunk = amountNum * revSharePct;
    const cbShare       = worldRevChunk * cbCutPct;
    const partnerShare  = worldRevChunk - cbShare;
    const afterRevShare = amountNum - worldRevChunk;
    const taxAmount     = taxPct > 0 ? afterRevShare * taxPct : 0;
    const recipientAmt  = afterRevShare - taxAmount;

    return { worldRevChunk, cbShare, partnerShare, afterRevShare, taxAmount, recipientAmt, revSharePct, cbCutPct, taxPct };
  }, [amountNum, worldConfig, cbConfig]);

  const hasFees = fees && (fees.worldRevChunk > 0 || fees.taxAmount > 0);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/holders/${fromHolder.id}/tip`, {
        toHolderId,
        worldId: selectedBalWorldId,
        amount,
        reason,
        currency: selectedBalCurrency,
      }).then((r) => r.data),
    onSuccess: () => {
      setDone(true);
      qc.invalidateQueries({ queryKey: ['holders'] });
      setTimeout(onClose, 1800);
    },
  });

  const fmt8 = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 8, maximumFractionDigits: 8 });
  const pct  = (n: number) => `${(n * 100).toFixed(4).replace(/\.?0+$/, '')}%`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }} transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        className="relative w-full max-w-lg glass-card p-6 z-10 space-y-5 overflow-y-auto max-h-[92vh]"
        style={{ borderColor: 'rgba(251,191,36,0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <Gift className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Gift / Tip</h3>
              <p className="text-[10px] text-slate-600">From: <span className="text-slate-400 font-mono">{fromHolder.name}</span></p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-600 hover:text-slate-400 hover:bg-white/5 transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        {done ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <CheckCircle2 className="w-12 h-12 text-emerald-400" />
            <p className="text-sm font-semibold text-white">Gift sent!</p>
            <p className="text-xs text-slate-500">
              {fees ? fmt8(fees.recipientAmt) : amount} UKC delivered to recipient
            </p>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); if (confirmed) mutation.mutate(); }} className="space-y-4">
            {/* Recipient */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Recipient Holder</label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by name, email or ID…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={inputCls + ' pl-9'}
                />
              </div>
              <select
                value={toHolderId}
                onChange={(e) => setToHolderId(e.target.value)}
                required
                className="w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/40 transition-colors cursor-pointer"
              >
                <option value="">Select recipient…</option>
                {filteredRecipients.map((h) => (
                  <option key={h.id} value={h.id}>{h.name} — {h.email} ({h.id.slice(0, 8)}…)</option>
                ))}
              </select>
            </div>

            {/* Currency / World */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Currency (your balance)</label>
              <select
                value={balanceKey}
                onChange={(e) => {
                  const key = e.target.value;
                  setBalanceKey(key);
                  setWorldId(key.split(':')[0] ?? '');
                  setAmount('');
                }}
                required
                className="w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/40 transition-colors cursor-pointer"
              >
                <option value="">Select currency…</option>
                {balances.filter((b) => parseFloat(b.balance) > 0).map((b) => (
                  <option key={`${b.worldId}:${b.currency}`} value={`${b.worldId}:${b.currency}`}>
                    {b.currency} — {parseFloat(b.balance).toFixed(8)} available (world: {b.worldId.slice(0, 8)}…)
                  </option>
                ))}
              </select>
              {balances.filter((b) => parseFloat(b.balance) > 0).length === 0 && (
                <p className="text-[10px] text-amber-500/80 mt-1">This holder has no balance to gift</p>
              )}
            </div>

            {/* Amount */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Gross Amount {worldId && <span className="text-slate-700 normal-case">(max {available.toFixed(8)})</span>}
              </label>
              <input
                type="number"
                step="0.00000001"
                min="0.00000001"
                max={available}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00000000"
                required
                disabled={!worldId}
                className={inputCls + (overBalance ? ' border-red-500/50' : '')}
              />
              {overBalance && (
                <p className="text-[10px] text-red-400 mt-1">Exceeds available balance ({available.toFixed(8)})</p>
              )}
            </div>

            {/* ── Fee Breakdown ── */}
            {worldId && amountNum > 0 && (
              <div
                className="rounded-xl border overflow-hidden"
                style={{ background: 'rgba(0,0,0,0.3)', borderColor: 'rgba(255,255,255,0.06)' }}
              >
                <div className="px-4 py-2.5 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Fee Breakdown</p>
                  {hasFees && (
                    <p className="text-[10px] text-slate-600 mt-0.5">
                      Applicable world and CB revenue shares will be deducted before delivery
                    </p>
                  )}
                </div>
                <div className="px-4 py-3 space-y-1.5 text-xs font-mono">
                  {/* Gross */}
                  <div className="flex justify-between text-slate-300">
                    <span className="text-slate-500 font-sans">Gross amount</span>
                    <span>{fmt8(amountNum)} UKC</span>
                  </div>

                  {fees && fees.worldRevChunk > 0 && (
                    <>
                      <div className="flex justify-between text-red-400">
                        <span className="font-sans text-slate-500">
                          World rev share <span className="text-slate-600">({pct(fees.revSharePct)})</span>
                        </span>
                        <span>−{fmt8(fees.worldRevChunk)}</span>
                      </div>
                      <div className="pl-4 space-y-1">
                        <div className="flex justify-between text-[10px] text-slate-500">
                          <span className="flex items-center gap-1">
                            <ArrowRight className="w-2.5 h-2.5" />
                            CB cut <span className="text-slate-600 ml-1">({pct(fees.cbCutPct)} of rev share)</span>
                          </span>
                          <span className="text-cyan-600">{fmt8(fees.cbShare)}</span>
                        </div>
                        <div className="flex justify-between text-[10px] text-slate-500">
                          <span className="flex items-center gap-1">
                            <ArrowRight className="w-2.5 h-2.5" />
                            Partner world
                          </span>
                          <span className="text-purple-500">{fmt8(fees.partnerShare)}</span>
                        </div>
                      </div>
                    </>
                  )}

                  {fees && fees.taxAmount > 0 && (
                    <div className="flex justify-between text-red-400">
                      <span className="font-sans text-slate-500">
                        Tax <span className="text-slate-600">({pct(fees.taxPct)} after rev share)</span>
                      </span>
                      <span>−{fmt8(fees.taxAmount)}</span>
                    </div>
                  )}

                  {fees && !hasFees && (
                    <div className="text-[10px] text-slate-600 text-center py-1">No fees configured for this world</div>
                  )}

                  {/* Recipient final */}
                  <div
                    className="flex justify-between pt-2 mt-1 border-t font-semibold"
                    style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                  >
                    <span className="font-sans text-slate-400">Recipient receives</span>
                    <span className={fees ? 'text-emerald-400' : 'text-slate-300'}>
                      {fees ? fmt8(fees.recipientAmt) : fmt8(amountNum)} UKC
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Reason</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Great work on the quest!"
                required
                maxLength={200}
                className={inputCls}
              />
            </div>

            {/* Irreversibility warning + confirmation */}
            <div
              className="flex items-start gap-3 rounded-xl p-3.5 border"
              style={{ background: 'rgba(251,191,36,0.05)', borderColor: 'rgba(251,191,36,0.2)' }}
            >
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-amber-300">Immediate & irreversible</p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  This transfer cannot be undone. Applicable revenue share and tax will be deducted before delivery.
                  {fees ? <> The recipient will receive <span className="text-emerald-400 font-mono">{fmt8(fees.recipientAmt)} UKC</span>.</> : null}
                </p>
                <label className="flex items-center gap-2 mt-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="accent-amber-400 w-3.5 h-3.5"
                  />
                  <span className="text-xs text-slate-400">I understand and confirm this action</span>
                </label>
              </div>
            </div>

            {mutation.isError && (
              <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Transfer failed.'}
              </p>
            )}

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 py-2.5 rounded-xl text-sm text-slate-400 border border-white/8 hover:border-white/15 hover:text-slate-300 transition-all">
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isPending || !confirmed || overBalance || !toHolderId || !worldId || !amount || !reason}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40 transition-all"
                style={{ background: 'linear-gradient(135deg,rgba(251,191,36,0.2),rgba(245,158,11,0.3))', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24' }}
              >
                {mutation.isPending
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                  : <><Gift className="w-3.5 h-3.5" /> Send Gift / Tip</>}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

function totalUkcOnly(balances: Holder['balances']): string {
  const sum = balances
    .filter((b) => b.currency === 'UKC')
    .reduce((acc, b) => acc + parseFloat(b.balance || '0'), 0);
  if (sum === 0) return '0';
  if (sum >= 1e6) return `${(sum / 1e6).toFixed(2)}M`;
  if (sum >= 1e3) return `${(sum / 1e3).toFixed(2)}K`;
  return sum.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function distinctCurrencyCount(balances: Holder['balances']): number {
  return new Set(balances.map((b) => b.currency)).size;
}

function RegisterHolderModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.role);
  const authWorldIds = useAuthStore((s) => s.worldIds);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedWorldIds, setSelectedWorldIds] = useState<string[]>([]);
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);

  const { data: worlds = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  const worldOptions = role === 'WORLD_OWNER'
    ? worlds.filter((w) => authWorldIds.includes(w.id))
    : worlds;

  const toggleWorld = (worldId: string) => {
    setSelectedWorldIds((prev) =>
      prev.includes(worldId) ? prev.filter((id) => id !== worldId) : [...prev, worldId],
    );
  };

  const mutation = useMutation({
    mutationFn: (data: { name: string; email: string; worldIds: string[] }) =>
      api.post('/holders', data).then((r) => r.data),
    onSuccess: (data: { id: string; name: string }) => {
      setCreated(data);
      qc.invalidateQueries({ queryKey: ['holders'] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedWorldIds.length === 0) return;
    mutation.mutate({ name: name.trim(), email: email.trim().toLowerCase(), worldIds: selectedWorldIds });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        className="relative w-full max-w-lg glass-card p-6 z-10 overflow-y-auto max-h-[90vh]"
        style={{ borderColor: 'rgba(0,212,255,0.2)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Register Holder</h3>
              <p className="text-[10px] text-slate-600">Create a new holder account</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-300 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {created ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4" />
              Holder "{created.name}" registered
            </div>
            <p className="text-xs text-slate-500">The holder has been created and can now receive UKC funding from their worlds.</p>
            <button onClick={onClose} className="w-full btn-primary py-2.5 rounded-xl text-sm font-semibold mt-2">Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Full Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Alice Smith"
                required
                minLength={2}
                maxLength={100}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="alice@example.com"
                required
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Worlds <span className="text-slate-700 normal-case">(select all that apply)</span>
              </label>
              {worldOptions.length === 0 ? (
                <p className="text-xs text-slate-600">No worlds available</p>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {worldOptions.map((w) => (
                    <label
                      key={w.id}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-white/8 hover:border-cyan-500/20 hover:bg-cyan-500/5 cursor-pointer transition-all"
                    >
                      <input
                        type="checkbox"
                        checked={selectedWorldIds.includes(w.id)}
                        onChange={() => toggleWorld(w.id)}
                        className="accent-cyan-500 w-3.5 h-3.5"
                      />
                      <span className="text-xs text-slate-300">{w.name}</span>
                    </label>
                  ))}
                </div>
              )}
              {selectedWorldIds.length === 0 && (
                <p className="text-[10px] text-amber-500/80 mt-1">Select at least one world</p>
              )}
            </div>

            {mutation.isError && (
              <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to register holder.'}
              </p>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-slate-400 border border-white/8 hover:border-white/15 hover:text-slate-300 transition-all">
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isPending || selectedWorldIds.length === 0}
                className="flex-1 btn-primary flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
              >
                {mutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Registering...</> : <><Plus className="w-3.5 h-3.5" /> Register</>}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

export function HoldersPage() {
  const [showModal, setShowModal] = useState(false);
  const [editHolder, setEditHolder] = useState<Holder | null>(null);
  const [giftHolder, setGiftHolder] = useState<Holder | null>(null);
  const [seedHolder, setSeedHolder] = useState<Holder | null>(null);
  const [fundHolder, setFundHolder] = useState<Holder | null>(null);
  const role = useAuthStore((s) => s.role);
  const isReadOnly = role === 'ADMIN_READONLY';
  const qc = useQueryClient();

  const { data: holders = [], isLoading } = useQuery<Holder[]>({
    queryKey: ['holders'],
    queryFn: () => api.get('/holders').then((r) => r.data),
  });

  const HOLDERS_PER_PAGE = 25;
  const [holderSearch, setHolderSearch] = useState('');
  const filteredHolders = useMemo(() => {
    const q = holderSearch.trim().toLowerCase();
    return q
      ? holders.filter((h) =>
          h.name.toLowerCase().includes(q) ||
          h.email.toLowerCase().includes(q))
      : holders;
  }, [holders, holderSearch]);
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filteredHolders.length / HOLDERS_PER_PAGE));
  const paginatedHolders = useMemo(
    () => filteredHolders.slice((page - 1) * HOLDERS_PER_PAGE, page * HOLDERS_PER_PAGE),
    [filteredHolders, page],
  );

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/holders/${id}`, { isActive }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['holders'] });
    },
  });

  return (
    <div className="p-8">
      <AnimatePresence>
        {showModal && <RegisterHolderModal onClose={() => setShowModal(false)} />}
        {editHolder && <EditHolderModal holder={editHolder} onClose={() => setEditHolder(null)} />}
        {giftHolder && <GiftTipModal fromHolder={giftHolder} onClose={() => setGiftHolder(null)} />}
        {seedHolder && <SeedUkcModal holder={seedHolder} onClose={() => setSeedHolder(null)} />}
        {fundHolder && <FundFromWorldModal holder={fundHolder} onClose={() => setFundHolder(null)} />}
      </AnimatePresence>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-xl font-bold text-white">Holders</h2>
          <p className="text-sm text-slate-600 mt-0.5">{holders.length} registered holder{holders.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportCsv(holders, `holders-${new Date().toISOString().slice(0,10)}.csv`, [
              { key: 'id', label: 'Holder ID' },
              { key: 'name', label: 'Name' },
              { key: 'email', label: 'Email' },
              { key: 'isActive', label: 'Active' },
              { key: 'createdAt', label: 'Created At' },
            ])}
            className="flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-xl border border-white/10 text-slate-400 hover:text-white hover:border-white/20 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
          {!isReadOnly && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 btn-primary text-xs font-semibold px-4 py-2 rounded-xl"
            >
              <Plus className="w-3.5 h-3.5" />
              Register Holder
            </button>
          )}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="glass-card overflow-hidden"
      >
        <div className="h-px bg-gradient-to-r from-transparent via-cyan-500/25 to-transparent" />

        {/* Search bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.04]">
          <Search className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
          <input
            type="text"
            value={holderSearch}
            onChange={(e) => { setHolderSearch(e.target.value); setPage(1); }}
            placeholder="Search by name or email…"
            className="flex-1 bg-transparent text-sm text-slate-300 placeholder-slate-700 focus:outline-none"
          />
          {holderSearch && (
            <button onClick={() => { setHolderSearch(''); setPage(1); }} className="text-slate-600 hover:text-slate-400">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(0,212,255,0.08)' }}>
              {['Holder', 'Email', 'Worlds', 'UKC Balance', 'Currencies', 'Status', 'Actions', ''].map((h) => (
                <th key={h} className="text-left px-5 py-3.5 text-[10px] font-semibold uppercase tracking-widest text-slate-600">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
            ) : filteredHolders.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-16 text-center">
                  <Wallet className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">{holderSearch ? 'No holders match your search' : 'No holders registered yet'}</p>
                  {!holderSearch && role === 'CENTRAL_BANK_ADMIN' && (
                    <p className="text-slate-700 text-xs mt-1">Register a holder to begin issuing UKC</p>
                  )}
                </td>
              </tr>
            ) : (
              paginatedHolders.map((h, idx) => (
                <motion.tr
                  key={h.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.04, duration: 0.3 }}
                  className="table-row-hover"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500/15 to-cyan-500/15 border border-emerald-500/15 flex items-center justify-center">
                        <Wallet className="w-3.5 h-3.5 text-emerald-400/70" />
                      </div>
                      <div>
                        <div className="font-semibold text-white text-sm">{h.name}</div>
                        <div className="font-mono text-[10px] text-slate-700">{h.id.slice(0, 8)}…</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-slate-400 text-xs">{h.email}</td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1">
                      {h.worldIds.map((wid) => (
                        <span key={wid} className="font-mono text-[10px] text-slate-600 bg-white/[0.03] px-2 py-0.5 rounded">
                          {wid.slice(0, 8)}…
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="font-mono text-sm text-emerald-400 font-semibold">
                      {totalUkcOnly(h.balances)} UKC
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="font-mono text-sm text-slate-400">
                      {distinctCurrencyCount(h.balances) || '—'}
                    </span>
                  </td>
                  <td className="px-5 py-4"><ActiveBadge active={h.isActive} /></td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5">
                      {!isReadOnly && (
                        <button
                          onClick={() => setEditHolder(h)}
                          title="Edit holder"
                          className="p-1.5 rounded-lg text-slate-600 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {!isReadOnly && (
                        <button
                          onClick={() => setGiftHolder(h)}
                          title="Gift / Tip"
                          className="p-1.5 rounded-lg text-slate-600 hover:text-amber-400 hover:bg-amber-500/10 transition-all"
                        >
                          <Gift className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {role === 'CENTRAL_BANK_ADMIN' && (
                        <button
                          onClick={() => setSeedHolder(h)}
                          title="Seed UKC balance"
                          className="p-1.5 rounded-lg text-slate-600 hover:text-yellow-400 hover:bg-yellow-500/10 transition-all"
                        >
                          <Coins className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {role === 'WORLD_OWNER' && (
                        <button
                          onClick={() => setFundHolder(h)}
                          title="Fund from world revenue"
                          className="p-1.5 rounded-lg text-slate-600 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                        >
                          <Sprout className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {!isReadOnly && (
                        <button
                          onClick={() => toggleMutation.mutate({ id: h.id, isActive: !h.isActive })}
                          title={h.isActive ? 'Deactivate' : 'Activate'}
                          disabled={toggleMutation.isPending}
                          className={`p-1.5 rounded-lg transition-all ${
                            h.isActive
                              ? 'text-emerald-500 hover:text-red-400 hover:bg-red-500/10'
                              : 'text-slate-600 hover:text-emerald-400 hover:bg-emerald-500/10'
                          }`}
                        >
                          {h.isActive ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Link
                      to={`/holders/${h.id}`}
                      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-cyan-400 transition-colors"
                    >
                      View <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-white/[0.04]">
            <span className="text-xs text-slate-600">
              Page {page} of {totalPages} &bull; {filteredHolders.length}{holderSearch ? ` / ${holders.length}` : ''} holders
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-slate-400 border border-white/[0.06] disabled:opacity-30 hover:border-white/10 transition-colors"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
